<template>
  <!-- ============================================================
       No.9 代行者権限付与：管理者側「代行者 設定／詳細」
       採択者を起点に、紐づく代行者の追加（招待）・氏名/有効期限の編集・解除を行う。
       ============================================================ -->
  <div class="CmsAgentEdit">
    <div class="contentsWidth mx-auto mt-4">
      <div>
        <p class="title mb-0">代行者管理 - 設定</p>
        <hr class="title">
      </div>

      <!-- 対象報告者 -->
      <div class="target-box my-3 px-3 py-2">
        <span class="bold mr-2">対象報告者</span>
        {{ applicant.name }}（{{ applicant.year }}年度 ／ {{ applicant.series }}）
      </div>

      <!-- 紐づく代行者の一覧 -->
      <div class="mt-3">
        <div class="bold mb-2">紐づく代行者</div>
        <b-table striped hover
          table-class="cmsAgentTable"
          thead-class="tableHead"
          :items="agents"
          :fields="header"
          show-empty>
          <template v-slot:empty>
            <div class="flex flexCenter">代行者は登録されていません（登録は採択者ご本人が行います）。</div>
          </template>
          <template v-slot:cell(no)="row">
            {{ row.index + 1 }}
          </template>
          <template v-slot:cell(term)="row">
            <span v-if="row.item.allowedTo">{{ formatTerm(row.item.allowedTo) }}</span>
            <span v-else class="text-muted">無期限</span>
          </template>
          <template v-slot:cell(status)="row">
            <span v-if="row.item.certified" class="badge badge-active">設定済</span>
            <span v-else class="badge badge-pending">招待中</span>
          </template>
          <template v-slot:cell(actions)="row">
            <!-- 操作列は幅が狭く、指定が無いと日本語が1文字単位で折り返される
                 （「解除」が「解／除」に割れる）。flex ＋ nowrap でリンク単位に並べ、
                 収まらない場合は行ごと折り返す。 -->
            <div class="action-links">
              <b-link @click="openEdit(row.item)">期限変更</b-link>
              <b-link v-if="!row.item.certified" @click="resendInvite(row.item)">招待再送</b-link>
              <b-link class="text-danger" @click="removeAgent(row.item.id)">解除</b-link>
            </div>
          </template>
        </b-table>
      </div>

      <!-- No.9 (B)：登録は採択者本人が行う（事務局は登録しない＝代行者が付く経路を1本にする）。 -->
      <div class="slim-note mt-4 px-3 py-3">
        <div class="bold mb-1">代行者の登録は採択者ご本人が行います</div>
        <p class="text-muted small mb-0">
          事務局では「有効期限の変更」「解除」「招待メールの再送」のみ行えます。<br>
          採択者が操作した場合は本人に通知されませんが、事務局が変更・解除した場合は採択者本人へ通知メールが送られます。
        </p>
      </div>

      <!-- ボタン -->
      <div class="mt-4 mb-5 flex flexCenter">
        <b-button class="btn btn-lg bold" @click="backToList">一覧に戻る</b-button>
      </div>

      <!-- 氏名・有効期限 編集モーダル（メール送信なしの更新のみ） -->
      <b-modal v-model="showEditModal" title="代行者を編集" hide-footer>
        <p class="text-muted small mb-3">
          氏名・有効期限を変更します。<strong>招待メールは送信されません。</strong>
        </p>
        <div class="flex flexLeft">
          <div class="mb-2 mr-3">
            <div class="small">姓</div>
            <b-form-input v-model="editTarget.sei" placeholder="例：事務"/>
          </div>
          <div class="mb-2">
            <div class="small">名</div>
            <b-form-input v-model="editTarget.mei" placeholder="例：花子"/>
          </div>
        </div>
        <!-- 有効期限（ログイン期限）：翌年度の再認可などで期限のみ更新できる -->
        <div class="mt-3 mb-2">
          <b-form-checkbox v-model="editTarget.setTerm">有効期限（ログイン期限）を設定する</b-form-checkbox>
          <div v-if="editTarget.setTerm" class="mt-2">
            <v-form-calendar
              name="editAllowedTo"
              label="この日までログイン可能"
              itemStyle="line"
              labelClass="left"
              input-class="pickerDateForm"
              :errMessage="[]"
              v-model="editTarget.allowedTo"/>
          </div>
          <div class="text-muted small mt-1">※チェックを外すと無期限（ログイン制限なし）になります。</div>
        </div>
        <b-alert :show="!!editErrMsg" variant="danger" class="mt-2 mb-0">{{ editErrMsg }}</b-alert>
        <div class="flex flexCenter mt-4">
          <b-button class="bold mr-2" @click="showEditModal = false">キャンセル</b-button>
          <b-button variant="primary" class="bold" @click="saveEdit">保存</b-button>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import api from '@/modules/api';

export default {
  name: 'cmsAgentsEdit',
  data() {
    return {
      applicantId: null,
      header: [
        { key: 'no', label: 'No', thStyle: { width: '52px' } },
        { key: 'name', label: '氏名', thStyle: { width: '150px' } },
        { key: 'email', label: 'メールアドレス' },
        { key: 'term', label: '有効期限', thStyle: { width: '180px' } },
        { key: 'status', label: '状態', thStyle: { width: '90px' } },
        // 幅の根拠：置ける幅 = 160 - セル余白14.4 = 145.6px。
        //   リンク2つ（設定済）→ 64+12+32 = 108px で1行に収まる
        //   リンク3つ（招待中）→ 1行目 64+12+64 = 140px、「解除」が2行目へ
        { key: 'actions', label: '操作', thStyle: { width: '160px' } },
      ],
      applicant: { name: '', year: '-', series: '-' },
      agents: [],
      errMsg: '',
      // 氏名・有効期限 編集モーダル用（メール送信なしの更新）
      showEditModal: false,
      editTarget: {
        id: null, sei: '', mei: '', setTerm: false, allowedTo: '',
      },
      editErrMsg: '',
    };
  },
  created() {
    this.applicantId = this.$route.params.applicant_user_id
      ? Number(this.$route.params.applicant_user_id) : null;
    this.getEdit();
  },
  methods: {
    async getEdit() {
      this.$store.dispatch('page/onLoading');
      const res = await api.send(`/api/cms/agents/edit/${this.applicantId}`).catch(() => false);
      if (res !== false) {
        const d = res.data.editList || {};
        this.applicant = d.applicant
          ? { name: d.applicant.applicant_name, year: d.applicant.year, series: d.applicant.apptype_name }
          : { name: '(不明)', year: '-', series: '-' };
        this.agents = (d.agents || []).map(a => ({
          id: a.agent_id,
          name: a.name,
          email: a.email,
          certified: a.is_certified,
          allowedTo: a.allowed_to,
        }));
      }
      this.$store.dispatch('page/offLoading');
    },
    // 422/400 応答を単一エラーメッセージへ整形
    toErrMsg(error, fallback) {
      const d = (error.response && error.response.data) || {};
      if (d.errMsg) {
        return d.errMsg;
      }
      if (d.message && typeof d.message === 'object') {
        if (d.message.errorMessage) {
          return d.message.errorMessage;
        }
        const msgs = Object.keys(d.message).reduce((acc, k) => acc.concat(d.message[k]), []);
        if (msgs.length > 0) {
          return msgs.join(' ');
        }
      }
      // 500（サーバーエラー）等で message が文字列の場合はそれを表示
      if (typeof d.message === 'string' && d.message) {
        return d.message;
      }
      return fallback;
    },
    // 有効期限バリデーション（設定する場合は終了日必須）。エラー文字列を返す（正常は空）
    validateTerm(setTerm, to) {
      if (setTerm && !to) {
        return '有効期限（ログイン期限）を設定する場合は日付を入力してください。';
      }
      return '';
    },
    // 有効期限の表示整形（終了日のみ）
    formatTerm(to) {
      return `${to} まで`;
    },
    // v-form-calendar は YYYY/MM/DD を返すため、送信時に YYYY-MM-DD へ正規化（backend の日付判定 date('Y-m-d',...) に合わせる）
    normalizeAllowedTo(v) {
      return v ? String(v).replace(/\//g, '-') : v;
    },
    // 氏名・有効期限 編集モーダルを開く（concat された name を姓・名に分解）
    openEdit(item) {
      const [sei, ...meiParts] = (item.name || '').trim().split(/\s+/);
      this.editTarget = {
        id: item.id,
        sei: sei || '',
        mei: meiParts.join(' '),
        setTerm: !!item.allowedTo,
        allowedTo: item.allowedTo || '',
      };
      this.editErrMsg = '';
      this.showEditModal = true;
    },
    // 氏名・有効期限を更新（招待メールは送信しない）
    async saveEdit() {
      this.editErrMsg = '';
      const sei = this.editTarget.sei.trim();
      const mei = this.editTarget.mei.trim();
      if (!sei || !mei) {
        this.editErrMsg = '姓・名を入力してください。';
        return;
      }
      const termErr = this.validateTerm(this.editTarget.setTerm, this.editTarget.allowedTo);
      if (termErr) {
        this.editErrMsg = termErr;
        return;
      }
      this.$store.dispatch('page/onWaiting');
      const params = {
        sei,
        mei,
        allowed_to: this.editTarget.setTerm ? this.normalizeAllowedTo(this.editTarget.allowedTo) : null,
      };
      const res = await api.send(`/api/cms/agents/update/${this.editTarget.id}`, params)
        .catch((error) => {
          this.editErrMsg = this.toErrMsg(error, '更新に失敗しました。');
          return false;
        });
      this.$store.dispatch('page/offWaiting');
      if (res === false) {
        return;
      }
      this.showEditModal = false;
      await this.getEdit();
      this.alert('氏名・有効期限を更新しました（招待メールは送信されません）。', false);
    },
    async removeAgent(id) {
      if (!await this.confirm('この代行者の紐づけを解除します。よろしいですか？')) {
        return;
      }
      this.$store.dispatch('page/onWaiting');
      const res = await api.send(`/api/cms/agents/destroy/${id}`)
        .catch(() => false);
      this.$store.dispatch('page/offWaiting');
      if (res === false) {
        this.alert('解除に失敗しました。', false);
        return;
      }
      await this.getEdit();
    },
    // 招待メールの再送（パスワード未設定の代行者のみ）
    async resendInvite(item) {
      if (!await this.confirm(`${item.name} さんに招待メール（パスワード設定のご案内）を再送します。よろしいですか？`)) {
        return;
      }
      this.$store.dispatch('page/onWaiting');
      const res = await api.send(`/api/cms/agents/resendInvite/${item.id}`)
        .catch((error) => {
          this.errMsg = this.toErrMsg(error, '再送に失敗しました。');
          return false;
        });
      this.$store.dispatch('page/offWaiting');
      if (res === false) {
        return;
      }
      await this.alert('招待メールを再送しました。', false);
    },
    backToList() {
      this.$router.push({ path: '/cms/agents/list' });
    },
  },
};
</script>

<style scoped>
.target-box {
  background-color: #DDD;
  border-radius: 4px;
  display: block;
}
.slim-note {
  background: #FFF8E1;
  border: 1px solid #F0E0A0;
  border-radius: 4px;
}
.add-box {
  background: #F4FAFB;
  border: 1px solid #D5EAF0;
  border-radius: 4px;
}
.add-field {
  width: 160px;
}
.add-field-wide {
  width: 320px;
}
.term-input {
  width: 200px;
}
.flexWrap {
  flex-wrap: wrap;
}
.badge-active {
  background: #0A8EA7;
  color: #fff;
}
.badge-pending {
  background: #f0ad4e;
  color: #fff;
}
/* 操作列：リンク単位で横並びにし、リンク内部では改行させない。
   収まらない場合のみリンクごと次行へ折り返す（row-gap で行間を確保）。 */
.action-links {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
}
.action-links a {
  white-space: nowrap;
}
</style>
